/**
 * Universal Sync Bridge
 *
 * CRITICAL: This is the ONLY component that bridges legacy storage to cloud
 * - Listens for sr:storage:set events from legacy modules
 * - Pushes data to cloud collections using MODULE_MAP
 * - Pulls cloud updates and emits sr:sync:hydrate for legacy hooks
 * - Modules remain unaware of cloud sync
 *
 * SUPABASE INTEGRATION (2025-01-21):
 * - Now uses real supabaseCloudClient instead of mock
 * - Falls back gracefully when Supabase is not configured
 * - Supports offline-first: localStorage is always source of truth
 */

import { devLog, devError } from '../utils/devLog';
import React, { useEffect, useRef, useCallback } from 'react';
import { MODULE_MAP, DATE_SCOPED_MODULES, SYNC_POLICY, ARRAY_COLLECTIONS, ORG_SCOPED_MODULES } from './moduleMap';
import { useVessel } from '../contexts/VesselContext';
import { isAzureApiConfigured } from '../lib/azureApiClient';
import { getOrgId } from '../utils/getOrgId';  // FT-05: Needed for org-scoped cloud doc IDs
import * as storageRouter from '../lib/storageRouter';  // TASK 2 FIX: Write to both localStorage + IndexedDB
import { toOrgScopedKey } from '../lib/storage';  // FT-144: Org-scoped keys for object hydration
import { pushOutbox, processOutbox, retryFailedItems } from '../lib/outbox';  // FT-234: Persist failed pushes for offline retry. FT-405: retryFailedItems revives sticky-'failed' items before each drain.
import { extractItems } from './extractItems';  // FT-331 amend: shared with outbox.js to eliminate parallel copy
import { resolvePushMergeWinner } from './resolvePushMergeWinner';  // FT-446: shared safe PUSH tie-break (leaf, no cycle) — used here AND in outbox.js
import { retainTombstone } from './tombstone';  // FT-458: sticky soft-delete retain at pull + push merge sites (leaf, no cycle) — mirrors outbox.js
import { mergeUnifiedLogs } from './mergeUnifiedLog';  // FT-482: append-only unifiedLog merge so local-only voyage log entries survive a cloud-wins pull
import { mergeListFields } from './mergeListFields';  // FT-565: config-driven list-field union (equipment photos + auditLog) at pull + push merge sites (leaf, no cycle) — mirrors outbox.js
import { pullFleetProfilePhotos } from './fleetProfilePhotoPull';  // FT-436 (VESSELS-ONLY): read-only cross-device fleet-card profile-photo pull (rides sr.photos.v1 → photoPullMerge; no LWW/outbox/justPulled change)
import { withAuditKeyLock } from '../utils/auditStorage';  // FT-515 round 5: share the SAME per-key write lock saveAuditLog/appendAuditEntry use, so a cloud pull-merge can never interleave with a same-tab audit save against the same combined key

// Re-export for backward compat — pre-amend tests + external callers imported
// extractItems from UniversalSyncBridge directly.
export { extractItems };

// Import Azure cloud client (matches Supabase interface)
import { azureCloudClient } from './azureCloudClient';

// Track 1 telemetry (staging-gated) — [SYNC-MERGE-DECISION] event
import { track1 } from '../utils/track1Telemetry';

// FT-365 Option B: user-boundary state reset helper (belt-and-braces).
// Extracted into its own module so the invariant is unit-testable without
// mounting the whole bridge with a full mocked React/VesselContext harness.
// See src/sync/__tests__/UniversalSyncBridge.reset.test.js.
import { resetSyncBridgeUserScopedState } from './syncBridgeReset';

/**
 * FT-365 v3 Layer B#4: in-flight sync abort registry.
 *
 * Every push/pull invocation registers an AbortController here. On user-boundary
 * wipe start or `sr:auth:expired`, `abortAllInflightSync()` iterates the set and
 * calls `.abort()` on each — the sync bridge's post-fetch handlers check the
 * signal and short-circuit (see wrapWithAbortSignal below), so stale responses
 * from the previous user never write to LS/IDB.
 *
 * We intentionally DO NOT rewire `apiRequest`'s fetch call to accept the signal
 * — the brief says "add AbortController AROUND fetches, don't change the sync
 * logic itself". Wrapping at the bridge layer discards the RESULT even if the
 * underlying fetch runs to completion. Belt-and-braces: apiRequest itself has
 * a 401 → sr:auth:expired flow (line 165) that will also fire on stale responses.
 */
const inflightAbortControllers = new Set();

/**
 * FT-365 v3 Layer B#4: wrap a promise-returning async op so its resolution is
 * short-circuited if `controller.signal.aborted` becomes true. Registers the
 * controller in `inflightAbortControllers` for the lifetime of the op.
 * Never throws — on abort, resolves to `{ success: false, aborted: true }`.
 */
async function wrapWithAbortSignal(controller, op) {
  inflightAbortControllers.add(controller);
  try {
    const result = await op();
    if (controller.signal.aborted) {
      // Response arrived AFTER the wipe — discard.
      return { success: false, aborted: true };
    }
    return result;
  } finally {
    inflightAbortControllers.delete(controller);
  }
}

/**
 * FT-365 v3 Layer B#4: called by authCacheWipe (and via `sr:auth:expired`) to
 * abort every in-flight push/pull so stale responses don't write to caches.
 * Fire-and-forget — the sync operations resolve to {aborted: true} and the
 * bridge's error handlers treat them as failed pushes (safe: they land in the
 * outbox for retry when the next user signs in, on their own account).
 */
export function abortAllInflightSync() {
  try {
    inflightAbortControllers.forEach((c) => {
      try { c.abort(); } catch (_) { /* non-fatal */ }
    });
    inflightAbortControllers.clear();
  } catch (_) { /* non-fatal */ }
}

// FT-365 v3 Layer B#4: also abort in-flight sync on session expiry.
if (typeof window !== 'undefined') {
  try {
    window.addEventListener('sr:auth:expired', () => {
      try { abortAllInflightSync(); } catch (_) { /* non-fatal */ }
    });
  } catch (_) { /* non-fatal */ }
}

/**
 * Normalize a timestamp of unknown type to epoch milliseconds.
 *
 * BUG-2026-07-02-SYNC-MERGE-TS-COERCE: Legacy writes stored `updatedAt` as ISO
 * strings via `new Date().toISOString()`; modern writes store `Date.now()`
 * Numbers. The pull-merge-push comparator at UniversalSyncBridge.js:509
 * previously compared them via `>=`, which coerces the string to NaN and makes
 * cloud always win when types mismatch. This helper normalizes both sides.
 *
 * Contract:
 * - finite Number → passthrough (including 0, negatives, MAX_SAFE_INTEGER)
 * - NaN / Infinity / -Infinity → 0
 * - ISO 8601 string → epoch ms via Date.parse
 * - unparseable string (incl. "", "not-a-date", integer-strings like "123456"
 *   which Date.parse returns NaN for in modern V8/JSC) → 0
 * - null / undefined / boolean / object / array → 0
 *
 * FT-446 (2026-08-15): BOTH the PULL gate (`resolvePullMergeWinner`) and the
 * PUSH gate (`resolvePushMergeWinner`) now treat a return of 0 (blank/unknown
 * timestamp) as CLOUD-wins — local wins ONLY when its ts is KNOWN and STRICTLY
 * NEWER. The earlier "local wins on ambiguity" default on the PUSH side was the
 * root cause of the cross-device sign-off clobber and has been removed; do NOT
 * reintroduce a `!localTs || localTs >= cloudTs` gate at any merge site.
 *
 * @param {unknown} v
 * @returns {number} epoch milliseconds (0 for invalid / non-parseable inputs)
 */
export const toEpochMs = (v) => {
  if (typeof v === 'number') {
    return Number.isFinite(v) ? v : 0;
  }
  if (typeof v === 'string' && v) {
    const parsed = Date.parse(v);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

/**
 * FT-296 — PULL-side shared-id winner gate (single source of truth).
 *
 * FT-446 (2026-08-15): this gate and the PUSH gate (`resolvePushMergeWinner`)
 * now share IDENTICAL semantics — for a SHARED id, the LOCAL copy wins ONLY when
 * its timestamp is KNOWN and STRICTLY NEWER than cloud's. On BLANK or EQUAL
 * timestamps → CLOUD wins (so deletes/archives don't resurrect and idempotent
 * re-pulls don't churn). (Previously this docstring claimed to "mirror" a PUSH
 * gate of `!localTs || localTs >= cloudTs`; that PUSH gate was the OPPOSITE and
 * caused the cross-device sign-off clobber — it has been replaced.)
 * Uses `toEpochMs` so Number-vs-ISO-string shapes compare correctly instead of
 * coercing to NaN.
 *
 * @param {object|null|undefined} localItem  existing local item for this id (or nullish)
 * @param {object} cloudItem                 incoming cloud item for this id
 * @returns {{ winner: 'local'|'cloud', localTs:number, cloudTs:number,
 *             localHasTs:boolean, cloudHasTs:boolean, localWins:boolean }}
 */
export const resolvePullMergeWinner = (localItem, cloudItem) => {
  const localHasTs = !!(localItem && (localItem.updatedAt || localItem.lastSaved));
  const cloudHasTs = !!(cloudItem && (cloudItem.updatedAt || cloudItem.lastSaved));
  const localTs = localItem ? toEpochMs(localItem.updatedAt || localItem.lastSaved || 0) : 0;
  const cloudTs = cloudItem ? toEpochMs(cloudItem.updatedAt || cloudItem.lastSaved || 0) : 0;
  const localWins = !!localItem && localHasTs && localTs > cloudTs;
  return { winner: localWins ? 'local' : 'cloud', localTs, cloudTs, localHasTs, cloudHasTs, localWins };
};

/**
 * FT-296 — PULL delta-merge (the authoritative pull-path shared-id merge).
 *
 * Unions existing LOCAL items with incoming CLOUD items keyed by `id`:
 *  - items with no `id` are skipped (REV-33 infinite-duplication guard);
 *  - for a shared id, `resolvePullMergeWinner` decides — LOCAL kept only when
 *    strictly-newer-with-known-ts, else CLOUD (FT-296 gate);
 *  - FT-182 archived exception: if CLOUD wins BUT the local item was archived,
 *    the cloud payload is taken while local archive status is preserved (so a
 *    stale un-archive from cloud can't resurrect an archived record).
 *
 * Pure + side-effect-free apart from the optional `onSharedId` callback, which
 * the bridge uses to fire [PULL-CLOBBER] telemetry. Extracted so the real merge
 * decision is unit-testable without mounting the React/VesselContext harness.
 *
 * @param {Array<Object>} existingItems  local items (may include no-id / archived)
 * @param {Array<Object>} cloudItems     incoming cloud items
 * @param {{ onSharedId?: (info:{ id:*, decision:object, localItem:object, cloudItem:object }) => void }} [opts]
 * @returns {{ merged: Array<Object>, skippedCount: number }}
 */
export const mergePullDelta = (existingItems, cloudItems, opts = {}) => {
  const { onSharedId, collection } = opts;
  const uniqueMap = new Map();
  let skippedCount = 0;

  (Array.isArray(existingItems) ? existingItems : []).forEach((i) => {
    if (i && i.id != null) uniqueMap.set(i.id, i);
    else if (i) skippedCount++;
  });

  (Array.isArray(cloudItems) ? cloudItems : []).forEach((i) => {
    if (!i || i.id == null) {
      if (i) skippedCount++;
      return;
    }
    const existingItem = uniqueMap.get(i.id);
    const decision = resolvePullMergeWinner(existingItem, i);
    if (existingItem && typeof onSharedId === 'function') {
      try { onSharedId({ id: i.id, decision, localItem: existingItem, cloudItem: i }); }
      catch (_) { /* telemetry must never crash a hydrate */ }
    }
    if (decision.localWins) {
      // FT-296: local edit strictly newer — KEEP it (already in map).
      // FT-565: but combine configured list fields (e.g. equipment photos /
      // auditLog) from the losing cloud copy, so a list addition made on
      // another device isn't discarded with the rest of its item. Additive
      // only; no-op (same reference) for unconfigured collections.
      uniqueMap.set(i.id, mergeListFields(collection, existingItem, i));
    } else {
      // Cloud wins (blank/equal/older local, or no local item).
      // FT-458: sticky tombstone retain — GENERALISES the old FT-182
      // archived-only preservation to ALL tombstone shapes (snake `deleted_at`,
      // camel `deletedAt`, status `'archived'`). If the local copy is
      // tombstoned but this cloud winner is NOT, re-apply the local tombstone
      // markers so a stale non-tombstoned cloud copy can never un-delete /
      // un-archive a locally-deleted record. retainTombstone is a no-op when
      // local isn't tombstoned or the winner already is.
      let cloudWinner = retainTombstone(existingItem, i);
      // FT-482: for voyage collections, merge unifiedLog so local entries survive pull
      if (collection === 'sr.operations_voyages.v1' && cloudWinner) {
        const localLog = existingItem ? existingItem.unifiedLog : undefined;
        cloudWinner = { ...cloudWinner, unifiedLog: mergeUnifiedLogs(localLog, cloudWinner.unifiedLog) };
      }
      // FT-565: generic, config-driven list-field merge — a SEPARATE path from
      // the FT-482 voyage special case above (voyages are not configured in
      // LIST_FIELD_MERGE, so this is a no-op for them). Combines configured
      // list fields from the losing LOCAL copy into the cloud winner.
      cloudWinner = mergeListFields(collection, cloudWinner, existingItem);
      uniqueMap.set(i.id, cloudWinner);
    }
  });

  return { merged: [...uniqueMap.values()], skippedCount };
};

/**
 * PUSH pull-merge-push union (bridge online push path).
 *
 * FT-565: extracted VERBATIM from the inline loop in processSyncQueue so the
 * real push merge decision is unit-testable (same pattern as `mergePullDelta`).
 * The offline-drain twin of this loop lives in lib/outbox.js (it cannot import
 * from this file — FT-331 cycle) and must be kept in step with it.
 *
 * @param {Array<Object>} cloudItems  items pulled from cloud (already unwrapped)
 * @param {Array<Object>} localItems  local items about to be pushed (already unwrapped)
 * @param {{ collection?: string }} [opts]
 * @returns {Array<Object>} merged items to push
 */
export const mergePushUnion = (cloudItems, localItems, opts = {}) => {
  const { collection } = opts;
  const uniqueMap = new Map();

  // Add cloud items first
  (Array.isArray(cloudItems) ? cloudItems : []).forEach(i => {
    if (i && i.id) uniqueMap.set(i.id, i);
  });

  // Add local items — safe tie-break: LOCAL wins ONLY when its ts is
  // KNOWN and STRICTLY NEWER than cloud (blank/equal/older → CLOUD).
  // FT-446 (2026-08-15) SAFETY-CRITICAL: this was `!localTs ||
  // localTs >= cloudTs`, the OPPOSITE of the safe PULL gate — a
  // no-timestamp local (ts=0) or an equal-ts local ALWAYS won and
  // clobbered a newer cloud copy, silently reverting maintenance
  // sign-offs across devices. Now routed through the shared
  // `resolvePushMergeWinner` leaf (identical semantics to
  // `resolvePullMergeWinner`) so both directions agree. A GENUINELY
  // newer local edit (real offline change with a later ts) STILL wins
  // and pushes — only blank/equal/older locals are stopped.
  // BUG-2026-07-02-SYNC-MERGE-TS-COERCE: `resolvePushMergeWinner`
  // normalizes both operands via toEpochMs internally.
  (Array.isArray(localItems) ? localItems : []).forEach(i => {
    if (!i || !i.id) return;
    const existing = uniqueMap.get(i.id);
    if (!existing) { uniqueMap.set(i.id, i); return; }
    const { localTs, cloudTs, localWins } = resolvePushMergeWinner(i, existing);
    if (localWins) { uniqueMap.set(i.id, i); }
    // else: cloud is same-or-newer / local blank — keep existing (no action)
    // FT-565: combine configured list fields (equipment photos / auditLog)
    // from the LOSING copy into the winner, so a photo added on one device
    // isn't discarded when another device's edit to the same item wins.
    // Additive only — never changes which record body won. No-op (same
    // reference) for collections not in LIST_FIELD_MERGE. Mirrored in
    // lib/outbox.js (offline drain).
    uniqueMap.set(i.id, mergeListFields(collection, uniqueMap.get(i.id), localWins ? existing : i));
    // FT-458 SAFETY-CRITICAL: the PUSH merge previously had NO tombstone
    // (not even FT-182 archived) retain — a locally-deleted item with an
    // older/equal ts LOST to the non-tombstoned cloud copy, so the
    // deletion never pushed and the item resurrected. Sticky-retain the
    // local tombstone onto the winner (ordered AFTER the tie-break, so it
    // only ADDS the delete marker — it never re-decides the record body,
    // preserving the FT-446 winner). No-op when local isn't tombstoned.
    uniqueMap.set(i.id, retainTombstone(i, uniqueMap.get(i.id)));
    // [SYNC-MERGE-DECISION] telemetry: only when winner is non-trivial
    // (types mismatched OR cloud won). Gated on track1.isOn() — no-op in prod.
    try {
      if (track1.isOn()) {
        const rawLocal = i.updatedAt || i.lastSaved || 0;
        const rawCloud = existing.updatedAt || existing.lastSaved || 0;
        const typesMismatched = typeof rawLocal !== typeof rawCloud;
        const cloudWon = !localWins;
        if (typesMismatched || cloudWon) {
          track1.log('[SYNC-MERGE-DECISION]', {
            id_h: track1.hashId(i.id),
            winner: localWins ? 'local' : 'cloud',
            local_ts_type: typeof rawLocal,
            cloud_ts_type: typeof rawCloud,
            local_ts_norm: localTs,
            cloud_ts_norm: cloudTs,
            types_mismatched: typesMismatched,
          });
        }
      }
    } catch (_) { /* telemetry never crashes a save */ }
  });

  return [...uniqueMap.values()];
};

/**
 * FT-515 round 4 — audit_log pull-merge SOURCE-OF-TRUTH fix.
 *
 * Every other ARRAY_COLLECTIONS module's local writes flow through
 * useStorageState (or, for drill_register, an explicit parallel
 * `localStorage.setItem` — see useDrillRegister.js), which keeps a
 * synchronous localStorage MIRROR of whatever IndexedDB holds. That mirror is
 * what the generic hydrate path (below, in handleCloudUpdate) reads as
 * "existing local data" before merging a cloud pull via
 * `localStorage.getItem(storageKey)`. audit_log is the ONE exception: FT-515
 * round 1 moved its storage to a single combined per-vessel IndexedDB key
 * (auditStorage.js, via storageRouter.readJSON/writeJSON) and nothing writes
 * that key to localStorage any more. In prod (USE_INDEXEDDB:true,
 * PARALLEL_STORAGE:false) that leaves the generic path's localStorage read
 * PERMANENTLY EMPTY for audit_log, so every pull treated "no local data" as
 * truth and the merge degenerated into a straight cloud overwrite — any
 * audit entry saved locally but not yet pushed to the server (e.g. a save
 * that lands the instant a background pull is in flight) was silently
 * dropped from IndexedDB the moment the pull completed. That is the exact
 * opposite of what an append-only compliance audit trail must guarantee.
 *
 * Fix: for audit_log ONLY, read "existing local" from the SAME place
 * auditStorage.js actually writes to (storageRouter -> IndexedDB in prod)
 * and run it through the SAME mergePullDelta gate the generic path uses, so
 * a cloud pull merges with what IndexedDB genuinely holds right now instead
 * of an empty stand-in. Deliberately narrow: this does NOT touch the general
 * ARRAY_COLLECTIONS mechanism other (working) collections rely on — their
 * localStorage mirror is confirmed intact (see FT-515 round 4 report).
 *
 * Exported so the behaviour is directly testable against the REAL
 * storageRouter/IndexedDB stack (same pattern as mergePullDelta above and
 * photoPullMerge.js) rather than only reachable through the fully mounted
 * UniversalSyncBridge component.
 *
 * FT-515 round 5 — WRITE LOCK (blocking issue found by senior review of round
 * 4): the read-merge-write cycle below is wrapped in `withAuditKeyLock`, the
 * SAME per-key promise-queue lock `auditStorage.js`'s `saveAuditLog` and
 * `appendAuditEntry` already use, keyed on the SAME combined audit key. Round
 * 4 ran this read-merge-write with no lock at all, so a pull-merge landing at
 * the same moment as a same-tab `appendAuditEntry` could interleave with it:
 * whichever write landed last silently clobbered the other's "before"
 * snapshot — either wiping the just-merged cloud entry from local storage, or
 * dropping a local not-yet-pushed entry (and forking the hash chain the very
 * next `logChange` call extends). Sharing the lock makes a pull-merge and a
 * same-tab save for the same vessel fully serialise: one always completes
 * (read AND write) before the other's read begins, so neither can observe a
 * stale "before" snapshot. See
 * UniversalSyncBridge.auditLogPullMergeLock.ft515-round5.test.js.
 *
 * @param {string} vesselId
 * @param {Array<Object>} cloudEntries - audit_log entries from the cloud pull (already unwrapped)
 * @returns {Promise<Array<Object>>} the merged array that was written to storage
 */
export const mergeAndPersistAuditLogPull = async (vesselId, cloudEntries) => {
  const storageKey = toOrgScopedKey(`audit_log_${vesselId}`);
  const cloudItems = Array.isArray(cloudEntries) ? cloudEntries : [];

  return withAuditKeyLock(storageKey, async () => {
    let existingItems = [];
    try {
      const existingReal = await storageRouter.readJSON(storageKey, []);
      existingItems = Array.isArray(existingReal) ? existingReal : [];
    } catch (e) {
      devError('[SYNC] FT-515: failed to read existing local audit_log via storageRouter:', e);
    }

    if (cloudItems.length > 0) {
      devLog(`[SYNC] FT-515: Merging ${cloudItems.length} audit_log updates into ${existingItems.length} existing LOCAL (IndexedDB) entries`);
    }

    const { merged, skippedCount } = mergePullDelta(existingItems, cloudItems, {
      collection: 'sr.audit_log.v1',
      onSharedId: ({ id, decision }) => {
        const { localTs, cloudTs, localHasTs, cloudHasTs, localWins } = decision;
        const localNewerLost = localHasTs && localTs > cloudTs && !localWins;
        track1.logAnomaly('[PULL-CLOBBER]', {
          collection: 'sr.audit_log.v1',
          orgId: getOrgId(),
          vesselId,
          id,
          winner: localWins ? 'local' : 'cloud',
          local_ts_norm: localTs,
          cloud_ts_norm: cloudTs,
          local_has_ts: localHasTs,
          cloud_has_ts: cloudHasTs,
          localNewerLost,
        });
      },
    });

    if (skippedCount > 0) {
      devLog(`[SYNC] FT-515: audit_log delta merge: ${existingItems.length} existing + ${cloudItems.length} cloud -> ${merged.length} total (${skippedCount} skipped - no IDs)`);
    }

    try {
      await storageRouter.writeJSON(storageKey, merged, { source: 'cloud-hydrate' });
    } catch (e) {
      devError(`[SYNC] FT-515: storageRouter.writeJSON failed for audit_log (${storageKey}):`, e);
    }

    return merged;
  });
};

/**
 * ENTERPRISE PATTERN: Runtime Configuration Resolver
 *
 * CRITICAL: This function is called at RUNTIME, not build time.
 * Returns the appropriate cloud client based on current environment state.
 *
 * Benefits:
 * - Lazy evaluation: configuration checked when needed, not during webpack bundle
 * - Observable: can be monitored and logged
 * - Testable: can be mocked in tests
 * - Resilient: adapts to backend availability changes
 *
 * @returns {Object} Cloud client interface (Azure or mock)
 */
function getCloudClient() {
  // Runtime check: is Azure API configured?
  if (isAzureApiConfigured()) {
    devLog('[SYNC] Using Azure cloud client (configured)');
    return azureCloudClient;
  }

  // Fallback: mock client for development
  devLog('[SYNC] Using mock cloud client (Azure not configured)');
  return {
    push: () => Promise.resolve(),
    pull: () => Promise.resolve([]),
    subscribe: (collection, vesselId, callback) => {
      devLog(`[SYNC] Mock subscribe to ${collection} (Azure has no real-time)`);
      return () => { };
    }
  };
}

/**
 * AUTO-HEAL ROUTINE (Added 2026-02-17)
 * Fixes the "Revision 26 Duplicate Mess" automatically on client load.
 * Removes duplicates and cleans up pollution.
 */
const cleanupStorageCorruption = (vesselId) => {
  if (!vesselId) return;
  try {
    devLog(`[AUTO-HEAL] Checking storage health for vessel ${vesselId}...`);
    let fixedCount = 0;

    // 1. DEDUPLICATE LISTS (Fixes the "3x Jobs" bug)
    ['maintenance_schedule_', 'maintenance_jobs_'].forEach(prefix => {
      const key = prefix + vesselId;
      const raw = localStorage.getItem(key);
      if (!raw) return;

      try {
        const items = JSON.parse(raw);
        if (Array.isArray(items) && items.length > 0) {
          // Use Map to keep unique IDs (last write wins)
          const uniqueMap = new Map();
          const noIdItems = [];

          items.forEach(i => {
            if (i && i.id) uniqueMap.set(i.id, i);
            else noIdItems.push(i);
          });

          const uniqueItems = [...uniqueMap.values(), ...noIdItems];

          if (uniqueItems.length < items.length) {
            devLog(`[AUTO-HEAL] Fixed duplicates in ${key}: ${items.length} -> ${uniqueItems.length}`);
            localStorage.setItem(key, JSON.stringify(uniqueItems));
            fixedCount++;
          }
        }
      } catch (e) {
        devError(`[AUTO-HEAL] Error processing ${key}:`, e);
      }
    });

    // 2. FT-86: custom_maintenance_jobs cleanup removed - fully migrated to maintenance_jobs (2026-03-28)

    // NOTE: "PURGE CORRUPT HISTORY" block removed (2026-02-21)
    // Push protection (revision 41) prevents the corruption this detected.
    // See NOTICEBOARD.md Phase B for details.

    if (fixedCount > 0) {
      devLog('[AUTO-HEAL] Storage corruption repaired successfully. Reloading UI...');
      // Force a UI refresh event
      window.dispatchEvent(new CustomEvent('sr:storage:healed'));
    }

  } catch (err) {
    console.error('[AUTO-HEAL] Failed:', err);
  }
};

/**
 * FT-365 Option B Phase 2: read the currently authenticated user id from LS
 * without going async — used by the sync-queue user-mismatch defensive filter
 * so a queued mutation from user A can never flush as user B.
 *
 * Reads sms_pro_user (the user record written by LoginScreen / getSession),
 * safely handling the not-logged-in / malformed-JSON / SSR cases. Returns null
 * on any failure — the caller treats null as "no assertion possible" and
 * skips the check, so failures never break the flush path.
 */
function getCurrentAuthUserId() {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem('sms_pro_user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed && parsed.id) || null;
  } catch (_) {
    return null;
  }
}

// FT-365 Option B Phase 2: log once at module load whether sync-queue items
// carry a userId field. Today they DON'T (this file doesn't stamp userId on
// pushes), so the user-mismatch assert in processSyncQueue is effectively a
// no-op. This log makes that visible in telemetry so a future coder wiring
// userId onto items can confirm the assert has teeth. Once userId is stamped,
// the field goes hot and the assert starts filtering / logging mismatches.
try {
  track1.log('[SYNC-QUEUE-USERID-FIELD]', {
    present: false,
    note: 'items do not carry userId today — mismatch assert is no-op until wired',
  });
} catch (_) { /* non-fatal */ }


export const UniversalSyncBridge = ({ children, enabled = true }) => {
  const { currentVesselId: vesselId, vessels } = useVessel();  // Get from VesselContext
  // FT-58 NOTE: Removed FT-57 vesselId null guard - it wasn't the fix for the sync loop
  // The real issue was sr.sms_documents.v1 being in ARRAY_COLLECTIONS (fixed in moduleMap.js)

  // FT-436 (fleet-card cross-device profile photos, VESSELS-ONLY) — a live ref to
  // the full server-scoped vessel LIST so the once-registered fleet profile-photo
  // pull reads the current list without a stale closure. Purely ADDITIVE: no
  // existing sync path reads this ref, and the pull below rides the separate
  // record-photo pipeline (sr.photos.v1 → photoPullMerge) — it does NOT touch the
  // LWW merge, the FT-453 justPulled cooldown, or the FT-234 outbox.
  const liveVesselsRef = useRef(vessels);
  useEffect(() => { liveVesselsRef.current = vessels; }, [vessels]);

  const syncQueue = useRef([]);
  const lastSyncTime = useRef({});
  const subscriptions = useRef({});
  const initialSyncComplete = useRef(false);

  // FT-15 Part 2: Track if startup reconciliation has been completed
  const reconciliationComplete = useRef(false);

  // 🛡️ FIX 1 (2026-03-09): Re-entrancy guard to prevent sync feedback loop
  const isProcessing = useRef(false);

  // 🛡️ FIX 2 (2026-03-09): Per-collection cooldown for 409 backoff
  // Map of collection → timestamp when cooldown expires
  // FT-245 NOTE: pushCooldowns is intentionally NOT reset on vessel switch.
  // A 409 reflects server-side conflict state that is independent of which
  // vessel the user is currently viewing locally — respect it across switches.
  const pushCooldowns = useRef(new Map());

  // FT-68 FIX: Track collections that were just pulled to prevent immediate re-push
  // This breaks the contamination cycle: pull → localStorage → push → repeat
  const justPulledCollections = useRef(new Map()); // Map<collection, timestamp>
  const JUST_PULLED_COOLDOWN = 5000; // 5 seconds cooldown after pull

  // FT-245: Per-vessel sync state reset.
  //
  // ROOT CAUSE: lastSyncTime is collection-keyed but vessel-agnostic. Without
  // resetting, the pull loop at line ~856 reads vessel A's `since` timestamp
  // when vesselId is now B, and the server (correctly) returns no rows for B
  // modified after A's last sync. The hydrate path then silently skips and B's
  // IDB stays empty.
  //
  // liveVesselIdRef is the SHARED-ACROSS-CLOSURES "current" vesselId that the
  // in-flight race guard inside performInitialSync (Change 3) consults. It is
  // updated synchronously inside this effect so that any in-flight A-loop can
  // detect that the user has switched to B and discard A's late writes to
  // lastSyncTime (which would re-poison B's freshly reset state and reproduce
  // the very bug FT-245 fixes). Do not collapse into vesselId — useCallback
  // closures capture vesselId by VALUE, so reading vesselId inside an A-loop
  // always returns A even after the switch. Only a ref bridges that gap.
  //
  // previousVesselId is initialised to the current vesselId so the FIRST mount
  // short-circuits the reset (lastSyncTime is already {} on first render —
  // no work to do, and the dev log line would be noise).
  //
  // initialSyncComplete and reconciliationComplete are reset because both are
  // semantic per-vessel flags. The 'sr:sync:status' syncing event must re-fire
  // for the new vessel; FT-15 reconciliation must re-evaluate the new vessel's
  // local-vs-empty-cloud state.
  //
  // EFFECT ORDERING — IMPORTANT: this effect MUST remain declared BEFORE the
  // startup useEffect at line ~1031. React commits effects in declaration
  // order within a single render. On a vesselId change both this effect and
  // the startup effect have vesselId in their dep arrays, so both re-run on
  // the same commit. This effect must run FIRST so that lastSyncTime is empty
  // (and liveVesselIdRef is updated) before the FT-234 IIFE inside the
  // startup effect calls performInitialSync() with B's vesselId. If a future
  // refactor moves these effects, that ordering invariant MUST be preserved.
  const liveVesselIdRef = useRef(vesselId);
  const previousVesselId = useRef(vesselId);
  useEffect(() => {
    // Always update the live ref FIRST and synchronously, even on initial
    // mount — the in-flight race guard depends on this ref being current
    // regardless of whether the previousVesselId guard short-circuits below.
    liveVesselIdRef.current = vesselId;

    if (previousVesselId.current === vesselId) return;

    devLog(`[SYNC] FT-245: vesselId changed (${previousVesselId.current ?? 'none'} → ${vesselId ?? 'none'}) — resetting lastSyncTime, initialSyncComplete, reconciliationComplete, justPulledCollections`);

    lastSyncTime.current = {};
    initialSyncComplete.current = false;
    reconciliationComplete.current = false;
    // See Change 2 below — also reset FT-68 cooldown map.
    justPulledCollections.current = new Map();

    previousVesselId.current = vesselId;
  }, [vesselId]);

  // Belt-and-braces (FT-365 Option B): if the reload invariant is ever
  // violated (flag OFF, service worker intercept, browser bug), reset
  // user-scoped state on sr:auth:userChanged to prevent cross-user leaks.
  // Under Option B (flag ON — default), window.location.reload() from
  // dispatchUserBoundaryReload fires ~0ms after the event, so this handler
  // usually won't finish before teardown — but if it does run first, or if
  // the reload is intercepted, we still short-circuit the leak. Mirrors the
  // subset of state Layer D's remount used to reset by unmounting the whole
  // subtree. See feedback_auth_boundary_reload_invariant.md.
  useEffect(() => {
    const handler = () => {
      // Delegates to the shared helper — see src/sync/syncBridgeReset.js.
      // Ref-mutation behaviour is identical to the pre-extraction inline body
      // (same 7 refs, same reset values, Map replacement not .clear()).
      resetSyncBridgeUserScopedState({
        syncQueue,
        lastSyncTime,
        subscriptions,
        initialSyncComplete,
        reconciliationComplete,
        justPulledCollections,
        pushCooldowns,
        // FT-365 Option B HARDENING: also tear down LIVE resources so no
        // previous-user resource survives even when the page does NOT reload.
        // isProcessing → cleared so a mid-drain boundary can't wedge the queue;
        // abortInflight → aborts in-flight fetches so a stale response can't
        // resolve into the next user's storage. Both exercised in the helper's
        // own tests (src/sync/__tests__/UniversalSyncBridge.reset.test.js).
        isProcessing,
        abortInflight: abortAllInflightSync,
      });
    };
    window.addEventListener('sr:auth:userChanged', handler);
    return () => window.removeEventListener('sr:auth:userChanged', handler);
  }, []);

  // Handle legacy storage events and push to cloud
  const handleStorageSet = useCallback(async (event) => {
    if (!enabled || !vesselId) {
      return;
    }

    const { module, vesselId: eventVesselId, key, value, timestamp, source } = event.detail;

    // FT-05: Skip vessel filter for org-scoped modules (fleet-wide data)
    // These modules have eventVesselId = 'default' (no vessel ID in key)
    const isOrgScoped = ORG_SCOPED_MODULES.has(module);

    // FT-68 / FT-457: Skip push if this collection was just pulled AND this write
    // is the confirmed cloud-hydrate ECHO of that pull (break the contamination
    // cycle). SAFETY-CRITICAL CHANGE (FT-457): the old code dropped EVERY write
    // inside the 5s cooldown with a bare `return` — including a GENUINE user save
    // in the first ~5s of a ~60-collection cold start (the ZZ-COLD 0/17 loss).
    // Now we drop ONLY the tagged echo (`source === 'cloud-hydrate'`). A genuine
    // (untagged) write, or ANY write whose provenance we can't confirm, FALLS
    // THROUGH to the normal push path below → fail-safe-KEEP. A redundant echo
    // push would be harmless (whole-doc LWW); a dropped genuine write is
    // unrecoverable — so KEEP is the safe default.
    const ft68Mapping = MODULE_MAP[module];
    const ft68CloudCollection = ft68Mapping && typeof ft68Mapping === 'string' ? ft68Mapping : (ft68Mapping?.collection || MODULE_MAP[module]);
    if (ft68CloudCollection) {
      const lastPulled = justPulledCollections.current.get(ft68CloudCollection);
      const inCooldown = lastPulled && (Date.now() - lastPulled) < JUST_PULLED_COOLDOWN;
      const isCloudHydrateEcho = source === 'cloud-hydrate';
      if (inCooldown && isCloudHydrateEcho) {
        // FT-365 Option B Phase 2 / FT-379: [SILENT-DROP] telemetry so drops in
        // the justPulled cooldown window stay visible in the ring buffer. Post
        // FT-457 this fires ONLY for a confirmed cloud-hydrate echo — an
        // expected, harmless drop — never for a genuine user write.
        try {
          track1.log('[SILENT-DROP]', {
            collection: ft68CloudCollection,
            reason: 'justPulled',
            ageMs: Date.now() - lastPulled,
            source: 'cloud-hydrate',
          });
          // FT-379: always-on anomaly path — persists + queues even when the
          // main telemetry gate is off. Kept for continuity of the denominator;
          // an echo drop is expected (not a loss) now that only echoes drop.
          track1.logAnomaly('[SILENT-DROP]', {
            collection: ft68CloudCollection,
            vesselId,
            reason: 'justPulled',
            source: 'cloud-hydrate',
          });
        } catch (_) { /* non-fatal */ }
        devLog(`[SYNC] FT-68/FT-457: DROPPED cloud-hydrate echo for ${module} - just pulled ${Date.now() - lastPulled}ms ago`);
        return;
      }
      // FT-457 fail-safe-KEEP: an UNTAGGED write inside the cooldown (a genuine
      // cold-start save) is NOT dropped — it falls through to the push queue
      // below. This inverts the pre-FT-457 fail-LOST behaviour.
      if (inCooldown && !isCloudHydrateEcho) {
        devLog(`[SYNC] FT-457: KEEPING untagged write for ${module} inside justPulled cooldown (fail-safe-KEEP)`);
      }
    }

    // Only sync events for current vessel (unless org-scoped)
    if (!isOrgScoped && eventVesselId !== vesselId) {
      return;
    }

    // Get cloud mapping from module map
    const mapping = MODULE_MAP[module];

    // Handle adapter-based modules (Phase 3)
    if (mapping && typeof mapping === 'object' && mapping.adapter) {
      const { collection, adapter } = mapping;
      const cloudDocs = adapter.toCloudDocuments(value, vesselId);

      // Push each doc to cloud
      for (const doc of cloudDocs) {
        syncQueue.current.push({
          collection,
          docId: doc.id,
          data: {
            ...doc,
            updatedAt: timestamp,
            syncedAt: Date.now()
          }
          // FT-25 REWRITE (2026-03-19): Removed isAdapterBased flag - HOR no longer uses adapter
        });
      }

      // Process queue and return early
      processSyncQueue();
      return;
    }

    // Legacy string mapping path
    let cloudCollection = mapping || MODULE_MAP[module];
    // FT-05: Org-scoped modules use org ID instead of vessel ID in cloud doc ID
    let cloudDocId = isOrgScoped
      ? `org_${getOrgId() || 'default'}_${module}`
      : `${vesselId}_${module}`;
    let cloudData = value;

    // Handle date-scoped modules specially
    if (module.startsWith('daily_checks_')) {
      cloudCollection = MODULE_MAP.daily_checks;
      const adapter = DATE_SCOPED_MODULES.daily_checks;
      const date = adapter.extractDate(module);
      if (date) {
        cloudDocId = adapter.createCloudId(vesselId, date);
        cloudData = {
          ...value,
          date,
          vesselId
        };
      }
    }

    // CRITICAL: Reject unknown keys to prevent data loss
    if (!cloudCollection) {
      devError('[SYNC] Unknown module rejected:', {
        module,
        vesselId,
        availableModules: Object.keys(MODULE_MAP),
        timestamp: new Date().toISOString()
      });
      return;
    }

    // 🛡️ ENHANCED PUSH PROTECTION v2: Cloud-aware guard (2026-03-05)
    // Prevents empty pushes from wiping cloud data — checks both arrays AND empty objects
    // The actual cloud-aware check happens in processSyncQueue (async) - this is the first line of defense
    const isEmptyArray = Array.isArray(cloudData) && cloudData.length === 0;
    const isEmptyObject = cloudData && typeof cloudData === 'object' && !Array.isArray(cloudData) &&
      Object.keys(cloudData).filter(k => !['vesselId', 'updatedAt', 'syncedAt'].includes(k)).length === 0;

    // Flag for cloud-aware checking in processSyncQueue
    const needsCloudCheck = ARRAY_COLLECTIONS.has(cloudCollection) && (isEmptyArray || isEmptyObject);
    if (needsCloudCheck) {
      devLog(`[SYNC] ⚠️ Empty data detected for ${cloudCollection} - will check cloud before pushing`);
    }

    // Add to sync queue
    // CRITICAL: Handle arrays properly - don't spread into object (that corrupts arrays)

    // FT-05 Commit 11 FIX: For org-scoped modules, send BARE org ID (no prefix)
    // Server expects raw orgId from JWT, comparing vesselId === userOrgId
    // Commit 10 sent "org_8304409a" but server expects "8304409a" → Fix: send bare orgId
    const effectiveVesselId = isOrgScoped ? (getOrgId() || 'default') : vesselId;

    // FT-168/FT-174: Filter org-scoped array data to prevent cross-org contamination
    // Only push records that belong to the current organization
    // FT-174: Now applies to ALL org-scoped modules (fleet_crew, fleet_risk_assessments, hours_of_rest)
    let filteredCloudData = cloudData;
    if (isOrgScoped && Array.isArray(cloudData)) {
      const currentOrgId = getOrgId();
      const beforeCount = cloudData.length;
      filteredCloudData = cloudData.filter(record => {
        // Keep records that have no organizationId (legacy) or match current org
        if (!record.organizationId) return true;
        return record.organizationId === currentOrgId;
      });
      const afterCount = filteredCloudData.length;
      if (beforeCount !== afterCount) {
        devLog(`[SYNC] FT-174: Filtered ${module} for push: ${beforeCount} → ${afterCount} (removed ${beforeCount - afterCount} cross-org records)`);
      }
    }

    const syncData = Array.isArray(filteredCloudData)
      ? {
        items: filteredCloudData,  // Wrap array in 'items' property
        _isArray: true,    // Flag to identify array-wrapped data
        _needsCloudCheck: needsCloudCheck,  // Flag for cloud-aware checking
        _sourceModule: module,  // FT-31: Track source localStorage key for filtering
        vesselId: effectiveVesselId,  // FT-05 Commit 10: Use org ID for org-scoped modules
        updatedAt: timestamp,
        syncedAt: Date.now()
      }
      : {
        ...filteredCloudData,  // FT-168: Use filtered data
        _needsCloudCheck: needsCloudCheck,  // Flag for cloud-aware checking
        _sourceModule: module,  // FT-31: Track source localStorage key for filtering
        vesselId: effectiveVesselId,  // FT-05 Commit 10: Use org ID for org-scoped modules
        updatedAt: timestamp,
        syncedAt: Date.now()
      };

    syncQueue.current.push({
      collection: cloudCollection,
      docId: cloudDocId,
      data: syncData
    });

    // Process queue (debounced)
    processSyncQueue();
  }, [vesselId, enabled]);

  // Process sync queue with batching
  const processSyncQueue = useCallback(async () => {
    // 🛡️ FIX 1 (2026-03-09): RE-ENTRANCY GUARD — prevent sync feedback loop
    if (isProcessing.current) {
      devLog('[SYNC] ⏳ Already processing, skipping (re-entrancy guard)');
      return;
    }
    isProcessing.current = true;

    try {
      if (syncQueue.current.length === 0) {
        return;
      }

      // FT-365 Option B Phase 2: user-mismatch defensive filter + telemetry.
      // If queue items ever start carrying `userId` (they don't today — logged
      // once at boot below), this guard drops any item whose userId doesn't
      // match the currently authenticated user so user A's queued mutation
      // can never flush as user B. Under the reload invariant this can only
      // happen if the reload was skipped/intercepted; the belt-and-braces
      // handler at line ~323 empties the queue on sr:auth:userChanged, so
      // this is a third layer of defense.
      try {
        const currentUserId = getCurrentAuthUserId();
        if (currentUserId) {
          const staleItems = syncQueue.current.filter(
            (item) => item && item.userId && item.userId !== currentUserId
          );
          if (staleItems.length > 0) {
            try {
              track1.log('[SYNC-QUEUE-USER-MISMATCH]', {
                stale: staleItems.length,
                total: syncQueue.current.length,
                staleUserIds: [...new Set(staleItems.map((i) => i.userId))],
                currentUserId,
              });
              // FT-379: always-on anomaly path. Scalars ONLY — never ship user
              // ids as content (staleUserIds/currentUserId deliberately omitted;
              // the server derives user_id from the JWT).
              track1.logAnomaly('[SYNC-QUEUE-USER-MISMATCH]', {
                staleCount: staleItems.length,
                total: syncQueue.current.length,
              });
            } catch (_) { /* non-fatal */ }
            syncQueue.current = syncQueue.current.filter(
              (item) => !item || !item.userId || item.userId === currentUserId
            );
          }
        }
      } catch (_) { /* non-fatal — assert must never break flush */ }

      const batch = syncQueue.current.splice(0, SYNC_POLICY.batchSize);

    for (const item of batch) {
      try {
        // 🛡️ FIX 2 (2026-03-09): 409 COOLDOWN CHECK — skip collections in cooldown
        const cooldownUntil = pushCooldowns.current.get(item.collection);
        if (cooldownUntil && Date.now() < cooldownUntil) {
          devLog(`[SYNC] ⏳ ${item.collection} in cooldown, skipping (${Math.round((cooldownUntil - Date.now()) / 1000)}s remaining)`);
          continue;
        }

        // 🛡️ CLOUD-AWARE GUARD: Check if we're about to push empty data over populated cloud
        if (item.data._needsCloudCheck) {
          const itemVesselId = item.data.vesselId;
          try {
            // FT-365 v3 R2 blocker 7: wrap in AbortController so a user-boundary
            // wipe mid-pull discards the response before it influences push logic.
            const ft365CloudCheckController = new AbortController();
            const pullResult = await wrapWithAbortSignal(
              ft365CloudCheckController,
              () => getCloudClient().pull(item.collection, itemVesselId, 0)
            );
            if (pullResult && pullResult.aborted) {
              devLog(`[SYNC] FT-365 v3: cloud-check pull aborted mid-flight for ${item.collection}`);
              // FT-379: [SYNC-ABORT-DROP] — cloud-check phase. The push is
              // skipped (continue) when its pre-flight pull is aborted.
              try {
                track1.logAnomaly('[SYNC-ABORT-DROP]', {
                  collection: item.collection,
                  vesselId,
                  id: item.docId,
                  phase: 'cloud-check',
                });
              } catch (_) { /* telemetry never breaks the flush */ }
              continue;
            }

            // 🔴 FT-49: Check for error signal (2026-03-23)
            // If pull failed, assume cloud has data and proceed with push
            if (pullResult?.error) {
              devLog(`[SYNC] FT-49 Cloud-check pull failed for ${item.collection} (status: ${pullResult.status}) - proceeding with push`);
              // Delete the flag and continue with push
              delete item.data._needsCloudCheck;
            }
            const cloudRecords = pullResult?.error ? [] : (Array.isArray(pullResult) ? pullResult : (pullResult?.data || []));

            // FT-86: custom_maintenance_jobs filtering removed - fully migrated to maintenance_jobs (2026-03-28)
            const relevantCloudRecords = cloudRecords || [];

            const cloudHasData = Array.isArray(relevantCloudRecords) && relevantCloudRecords.length > 0;

            if (cloudHasData) {
              // BLOCK: Cloud has data, local is empty — DO NOT WIPE
              devError(`[SYNC] ⛔ BLOCKED empty push for ${item.collection}`);
              devError(`[SYNC] Cloud has ${relevantCloudRecords.length} relevant records, local has 0`);
              devError(`[SYNC] Triggering force-pull to restore local data`);

              // Dispatch force-pull event to restore local state from cloud
              window.dispatchEvent(new CustomEvent('sr:sync:force-pull', {
                detail: { collection: item.collection, vesselId: itemVesselId, reason: 'empty-push-blocked' }
              }));
              continue; // Skip this push, move to next item
            }

            // ALLOW: Both cloud and local are empty — safe to sync (e.g., fresh vessel)
            devLog(`[SYNC] ✅ Empty push allowed for ${item.collection} — cloud is also empty`);
            // Continue to push

          } catch (pullError) {
            // If we can't verify cloud state, BLOCK the push to be safe
            devError(`[SYNC] ⛔ BLOCKED empty push for ${item.collection} — cloud check failed:`, pullError);
            continue; // Skip this push
          }
        }

        // 🛡️ PULL-MERGE-PUSH PATTERN (2026-03-07): Prevent partial pushes from wiping cloud data
        // For ARRAY_COLLECTIONS, always merge local with cloud before pushing
        // FIX 2026-03-08: UNWRAP cloud records before merging (prevents triple-nesting)
        // FIX 2026-03-09: RECURSIVE unwrap to handle any depth of nesting (cloud has 4+ levels)
        // FT-25 REWRITE (2026-03-19): Removed isAdapterBased check - HOR no longer uses adapter
        let dataToSync = item.data;
        if (ARRAY_COLLECTIONS.has(item.collection)) {
          const itemVesselId = item.data.vesselId;
          const rawLocalItems = item.data.items || (Array.isArray(item.data) ? item.data : []);

          // 🛡️ FIX 2026-03-09: Log BEFORE pull-merge-push
          devLog(`[SYNC] PUSHING: ${item.collection}, items: ${rawLocalItems?.length ?? 'no items array'}`);

          try {
            // 1. PULL: Get current cloud data
            // FT-365 v3 R2 blocker 7: wrap in AbortController so a mid-flight
            // wipe discards the pull result before it drives the merge/push.
            const ft365PullMergeController = new AbortController();
            const pullResult = await wrapWithAbortSignal(
              ft365PullMergeController,
              () => getCloudClient().pull(item.collection, itemVesselId, 0)
            );
            if (pullResult && pullResult.aborted) {
              devLog(`[SYNC] FT-365 v3: pull-merge-push pull aborted mid-flight for ${item.collection}`);
              continue;
            }

            // 🔴 FT-49: Check for error signal (2026-03-23)
            // If pull failed, skip merge and just push local data (don't destroy it)
            if (pullResult?.error) {
              devLog(`[SYNC] FT-49 Pull-merge-push pull failed for ${item.collection} (status: ${pullResult.status}) - pushing local only`);
              // Just push local items without merge
              const wrappedData = {
                vesselId: itemVesselId,
                items: rawLocalItems,
                _isArray: true,
                lastModified: Date.now()
              };
              // FT-365 v3 R2 blocker 7: wrap emergency push in AbortController.
              const ft365EmergencyPushController = new AbortController();
              const emergencyResult = await wrapWithAbortSignal(
                ft365EmergencyPushController,
                () => getCloudClient().push(item.collection, item.docId, wrappedData)
              );
              if (emergencyResult && emergencyResult.aborted) {
                devLog(`[SYNC] FT-365 v3: emergency push aborted mid-flight for ${item.collection}`);
                continue;
              }
              // 🛡️ FT-405 (2026-07-26): the FT-49 emergency push can FAIL (e.g.
              // offline returns {success:false, error:'offline mode'}). The old
              // code checked only `.aborted` then logged "PUSHED" and dropped the
              // item — the write vanished from both the in-memory queue (removed
              // at :695) and the persistent outbox, silently losing an offline
              // array-collection save (drills, crew, defects, etc.).
              //
              // We CANNOT `throw` here: this branch lives inside the pull-merge
              // `try` whose `catch (pullError)` a few lines below does a bare
              // `continue` (BLOCK the push, NO outbox write) — a throw would be
              // swallowed there and the item would still vanish. Instead we
              // persist to the outbox DIRECTLY, mirroring the outer catch's
              // FT-234 pushOutbox() call (see :970), then `continue`. Skip the
              // outbox write only for a genuine 409 (would fail again on retry).
              // Do NOT touch the aborted path above — aborted drops are intentional.
              if (!emergencyResult || !emergencyResult.success) {
                const emergencyErr = emergencyResult?.error || 'offline mode';
                const is409 = typeof emergencyErr === 'string' &&
                  (emergencyErr.includes('409') || emergencyErr.includes('Conflict'));
                if (is409) {
                  pushCooldowns.current.set(item.collection, Date.now() + 30000);
                  devLog(`[SYNC] ⏳ ${item.collection} in cooldown for 30s due to 409 error (emergency push)`);
                } else {
                  try {
                    await pushOutbox({
                      collection: item.collection,
                      type: 'update',
                      recordId: item.docId,
                      data: item.data,
                      vesselId: item.data?.vesselId || vesselId
                    });
                    devLog(`[SYNC] FT-405/FT-234: emergency push failed (${emergencyErr}) — queued to outbox for retry: ${item.collection}/${item.docId}`);
                  } catch (outboxError) {
                    devError(`[SYNC] FT-405: Failed to write emergency-push item to outbox:`, outboxError);
                  }
                }
                continue;
              }
              devLog(`[SYNC] PUSHED: ${item.collection} (local only, ${rawLocalItems?.length ?? 0} items)`);
              continue;
            }
            const cloudRecords = Array.isArray(pullResult) ? pullResult : (pullResult?.data || []);
            devLog(`[SYNC] PULLED: ${item.collection}, cloud records: ${cloudRecords?.length ?? 0}`);

            // 2. RECURSIVE UNWRAP: Extract individual items from any depth of nesting
            // See module-level `extractItems` (near top of file) for full contract.
            // FT-331 (2026-07-07): hoisted to module scope + belt-and-braces
            // wrapper-shape rejection to prevent FT-324-class ghost-doc poisoning.
            // 🛡️ FIX 2026-03-09: Unwrap BOTH cloud AND local items
            const cloudItems = extractItems(cloudRecords);
            const localItems = extractItems(rawLocalItems);

            devLog(`[SYNC] EXTRACTED: ${cloudItems.length} cloud items, ${localItems.length} local items`);

            // 3. MERGE: Union of cloud + local — see module-level
            // `mergePushUnion` (extracted verbatim by FT-565 so the real push
            // merge is unit-testable, same pattern as `mergePullDelta`).
            const mergedItems = mergePushUnion(cloudItems, localItems, { collection: item.collection });

            devLog(`[SYNC] MERGED: ${mergedItems.length} total items (cloud: ${cloudItems.length}, local: ${localItems.length})`);

            // 4. PREPARE: Update dataToSync with merged items
            if (item.data.items !== undefined) {
              // Data uses {items: [...]} wrapper
              dataToSync = { ...item.data, items: mergedItems };
            } else if (item.data._isArray) {
              // Data uses _isArray flag
              dataToSync = { ...item.data, items: mergedItems };
            } else {
              // Data is raw array (shouldn't happen, but handle it)
              dataToSync = mergedItems;
            }

          } catch (pullError) {
            // FAIL CLOSED: If we can't merge with cloud, BLOCK the push
            // The local data is safe in localStorage and will retry on next sync cycle
            devError(`[SYNC] ⛔ BLOCKED push for ${item.collection} — pull-merge failed:`, pullError.message);
            continue; // BLOCK the push — do NOT fall through
          }
        }

        // FT-365 v3 Layer B#4: register an AbortController so a user-boundary
        // wipe can invalidate the result before it lands. If aborted, the
        // wrapper returns { success:false, aborted:true } and the error branch
        // below treats it as a failed push (goes to outbox, safe).
        const ft365Controller = new AbortController();
        const result = await wrapWithAbortSignal(
          ft365Controller,
          () => getCloudClient().push(item.collection, item.docId, dataToSync)
        );
        if (result && result.aborted) {
          devLog(`[SYNC] FT-365 v3: push aborted mid-flight for ${item.collection} (user-boundary wipe or auth expired)`);
          // FT-379: [SYNC-ABORT-DROP] — a queued push was silently dropped when
          // its in-flight request was aborted (FT-373). Currently invisible.
          try {
            track1.logAnomaly('[SYNC-ABORT-DROP]', {
              collection: item.collection,
              vesselId,
              id: item.docId,
              phase: 'push',
            });
          } catch (_) { /* telemetry never breaks the flush */ }
          continue;
        }
        if (!result.success) {
          throw new Error(`Push failed: ${result.error || 'offline mode'}`);
        }
        // FT-245a follow-up: this write shares the same vessel-agnostic ref. The
        // upstream L171 vessel gate makes cross-vessel pushes ~impossible, but
        // `processSyncQueue` has a `[]` useCallback dep array (latent issue) so
        // a strict guard would need a generation counter. Deferred — see FT-245a.
        lastSyncTime.current[item.collection] = Date.now();
      } catch (error) {
        devError(`[SYNC] ❌ Failed to push ${item.collection}/${item.docId}:`, error);
        devError('[SYNC] Error details:', {
          collection: item.collection,
          docId: item.docId,
          errorMessage: error.message,
          errorStack: error.stack
        });

        // 🛡️ FIX 2 (2026-03-09): 409 BACKOFF — prevent infinite retry loop
        // If the error is a 409 Conflict, add this collection to cooldown
        const is409 = error.message?.includes('409') || error.message?.includes('Conflict');
        if (is409) {
          const cooldownDuration = 30000; // 30 seconds
          pushCooldowns.current.set(item.collection, Date.now() + cooldownDuration);
          devLog(`[SYNC] ⏳ ${item.collection} in cooldown for 30s due to 409 error`);
        }

        // 🛡️ FT-234 (2026-05-12): PERSIST FAILED PUSH TO OUTBOX
        // Critical for offline-first: if push fails (network down, server error),
        // write the item to persistent outbox so it survives page refresh
        // and can be retried on reconnect via processOutbox()
        // Skip 409 conflicts — those will fail again on retry
        if (!is409) {
          try {
            await pushOutbox({
              collection: item.collection,
              type: 'update',
              recordId: item.docId,
              data: item.data,
              vesselId: item.data?.vesselId || vesselId
            });
            devLog(`[SYNC] FT-234: Queued failed push to outbox for retry: ${item.collection}/${item.docId}`);
          } catch (outboxError) {
            devError(`[SYNC] FT-234: Failed to write to outbox:`, outboxError);
          }
        }
      }
    }
    } finally {
      // 🛡️ FIX 1 (2026-03-09): Always reset processing flag
      isProcessing.current = false;

      // 🛡️ FT-05 Commit 9 (2026-03-22): Continuous Queue Drain
      // After batch completes, check if queue still has items and continue processing
      // This fixes queue starvation where re-entrancy guard would silently drop calls
      // leaving items (like fleet_crew) orphaned at the back of the queue
      if (syncQueue.current.length > 0) {
        devLog(`[SYNC] FT-05: Queue has ${syncQueue.current.length} more items, continuing drain...`);
        // Use setTimeout to prevent stack overflow and allow React to breathe
        setTimeout(() => processSyncQueue(), 0);
      }
    }
  }, []);

  // Handle cloud updates and hydrate local storage
  const handleCloudUpdate = useCallback((collection, data) => {
    // FIXED 2026-02-17: Support multi-module hydration (e.g. maintenance_schedule + maintenance_history)
    // Old logic used .find() which only returned the first match, starving subsequent modules
    const moduleEntries = Object.entries(MODULE_MAP).filter(([key, mapping]) => {
      if (typeof mapping === 'string') return mapping === collection;
      if (typeof mapping === 'object') return mapping.collection === collection;
      return false;
    });

    if (moduleEntries.length === 0) return;

    // Dispatch hydration for EACH matching module
    moduleEntries.forEach(([baseKey, mapping]) => {
      // Handle adapter-based modules (Phase 3)
      if (mapping && typeof mapping === 'object' && mapping.adapter) {
        // Data is an array of cloud documents for adapter modules
        const localState = mapping.adapter.fromCloudDocuments(data);

        // Emit hydrate event with reconstructed local shape
        const event = new CustomEvent('sr:sync:hydrate', {
          detail: {
            module: baseKey,
            vesselId,
            value: localState,
            source: 'cloud',
            timestamp: Date.now()
          }
        });

        window.dispatchEvent(event);
        return;
      }

      // Legacy path: Handle date-scoped modules
      let storageKey = baseKey;
      if (baseKey === 'daily_checks' && data.date) {
        const adapter = DATE_SCOPED_MODULES.daily_checks;
        storageKey = adapter.createLegacyKey(data.date);
      }

      // Emit hydrate event for legacy hooks to consume
      // CRITICAL: Always use base module name for consistency
      // CRITICAL: Unwrap arrays that were wrapped in 'items' property
      // CRITICAL: Also detect and recover corrupted arrays (objects with numeric keys)
      let valueToHydrate = data;

      if (data && data._isArray && Array.isArray(data.items)) {
        // New format: properly wrapped array
        valueToHydrate = data.items;
      } else if (data && typeof data === 'object' && !Array.isArray(data)) {
        // Check if this looks like a corrupted array (has numeric keys like "0", "1", "2")
        const keys = Object.keys(data);
        const numericKeys = keys.filter(k => !isNaN(parseInt(k)));
        const metaKeys = ['vesselId', 'updatedAt', 'syncedAt', '_isArray', 'items'];
        const nonMetaKeys = keys.filter(k => !metaKeys.includes(k));

        // If most non-meta keys are numeric, this is a corrupted array
        if (numericKeys.length > 0 && numericKeys.length === nonMetaKeys.length) {
          devLog(`[SYNC] Recovering corrupted array for ${baseKey}: converting object with numeric keys back to array`);
          valueToHydrate = numericKeys
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map(k => data[k])
            .filter(item => item && typeof item === 'object');
        }
      }

      // 🛡️ FIX 2026-03-09: RECURSIVE UNWRAP for double/triple-nested data
      // Cloud corruption can result in: [{_isArray: true, data: {items: [actual_voyages]}}]
      // We need to recursively unwrap until we get the actual items
      const recursiveUnwrap = (items) => {
        if (!Array.isArray(items)) return items;

        // Check if this array contains ONLY wrapper objects (not actual data items)
        // A wrapper has: _isArray flag OR (data property with items)
        const isArrayOfWrappers = items.length > 0 && items.every(item =>
          item && typeof item === 'object' && (
            (item._isArray && Array.isArray(item.items)) ||
            (item.data && item.data._isArray && Array.isArray(item.data.items))
          )
        );

        if (isArrayOfWrappers) {
          // Unwrap one level and recurse
          const unwrapped = [];
          items.forEach(wrapper => {
            if (wrapper._isArray && Array.isArray(wrapper.items)) {
              unwrapped.push(...wrapper.items);
            } else if (wrapper.data && wrapper.data._isArray && Array.isArray(wrapper.data.items)) {
              unwrapped.push(...wrapper.data.items);
            }
          });
          devLog(`[SYNC] 🔄 Recursive unwrap: ${items.length} wrappers → ${unwrapped.length} items for ${baseKey}`);
          return recursiveUnwrap(unwrapped); // Recurse to handle deeper nesting
        }

        return items;
      };

      if (Array.isArray(valueToHydrate)) {
        valueToHydrate = recursiveUnwrap(valueToHydrate);
      }

      // ── FT-515 round 4: AUDIT_LOG MERGE-SOURCE BYPASS ───────────────────
      // The generic merge block below reads "existing local data" from
      // localStorage.getItem(...), which is PERMANENTLY EMPTY for audit_log
      // (its real local data lives in IndexedDB via storageRouter — see
      // mergeAndPersistAuditLogPull's doc comment above for the full root
      // cause). Handle the whole read-merge-write-dispatch cycle for
      // audit_log here, against the REAL local storage location, then stop —
      // everything below (the generic array merge, the ARRAY_COLLECTIONS
      // write, and the final sr:sync:hydrate dispatch) is for every OTHER
      // collection and must stay untouched.
      //
      // FT-515 round 5 — ECHO-LOOP FIX (blocking issue found by senior review
      // of round 4): the `justPulledCollections` marker MUST be set
      // SYNCHRONOUSLY, before `mergeAndPersistAuditLogPull` is called — NOT
      // inside its `.then()`. storageRouter.writeJSON's underlying IndexedDB
      // write fires its `sr:storage:set` event DURING the write, before the
      // returned promise resolves, so setting the marker only after the
      // helper's promise settles is too late: handleStorageSet's echo check
      // (~line 607, `justPulledCollections.current.get(...)`) already ran by
      // then, found no marker, and treated the cloud-hydrate echo as a
      // genuine local change — pushing it straight back to the server and
      // reproducing it on the next pull (wasted traffic, not data loss). This
      // mirrors the generic ARRAY_COLLECTIONS path below (~line "FT-68 FIX:
      // Mark this collection as just-pulled"), which sets the marker
      // immediately after KICKING OFF its write, not after awaiting it.
      if (collection === 'sr.audit_log.v1') {
        const cloudEntries = Array.isArray(valueToHydrate) ? valueToHydrate : [];
        justPulledCollections.current.set(collection, Date.now());
        devLog(`[SYNC] FT-68/FT-515: Marked ${collection} as just-pulled (cooldown ${JUST_PULLED_COOLDOWN}ms) BEFORE starting audit_log pull-merge write`);
        mergeAndPersistAuditLogPull(vesselId, cloudEntries).then((merged) => {
          window.dispatchEvent(new CustomEvent('sr:sync:hydrate', {
            detail: {
              module: baseKey,
              vesselId,
              value: merged,
              source: 'cloud',
              timestamp: Date.now(),
            }
          }));
        }).catch((e) => {
          devError('[SYNC] FT-515: audit_log pull-merge failed:', e);
        });
        return;
      }

      // FIX 2026-02-17: DELTA MERGE STRATEGY (Fixes "Disappearing Jobs")
      // We must merge incoming cloud updates with existing local data
      // instead of replacing the entire list (which causes data loss on partial syncs)

      // FT-88: Removed HYDRATE DEBUG logs that were showing in production console

      if (Array.isArray(valueToHydrate)) {
        // 1. Get existing data from storage
        // FT-116 FIX: Org-scoped modules (like fleet_risk_assessments) don't have vesselId suffix
        // FT-182 FIX: Use toOrgScopedKey to read from correct org-prefixed key (e.g., maintenance_jobs_orgId_vesselId)
        // Without this, archived items are missed during merge and get overwritten by cloud
        const isOrgScopedModule = ORG_SCOPED_MODULES.has(baseKey);
        const storageKey = toOrgScopedKey(isOrgScopedModule ? baseKey : `${baseKey}_${vesselId}`);
        const existingRaw = localStorage.getItem(storageKey);
        let existingItems = [];
        try {
          if (existingRaw) existingItems = JSON.parse(existingRaw);
          if (!Array.isArray(existingItems)) existingItems = [];
        } catch (e) {
          devError(`[SYNC] Error reading existing data for ${baseKey}:`, e);
        }

        // 2. MERGE: Union existing + cloud, keyed by id (REV-33: skip no-id).
        // FT-296: the per-id winner is decided by the shared, unit-tested
        // mergePullDelta / resolvePullMergeWinner gate — cloud wins EXCEPT when
        // a strictly-newer local edit exists (mirrors the push pre-flight gate),
        // and the FT-182 archived exception is preserved. The onSharedId
        // callback fires the FT-379 [PULL-CLOBBER] telemetry (needs the hook's
        // track1/getOrgId/collection/vesselId closure), recording the ACTUAL
        // winner. `localNewerLost` stays the real-loss oracle: post-fix it must
        // never be true because a fresher local edit is now KEPT, not clobbered.
        if (valueToHydrate.length > 0) {
          devLog(`[SYNC] Merging ${valueToHydrate.length} updates into ${existingItems.length} existing items for ${baseKey}`);
        }
        const { merged: mergedFromDelta, skippedCount } = mergePullDelta(
          existingItems,
          valueToHydrate.length > 0 ? valueToHydrate : [],
          {
            collection,  // FT-482: passed so mergePullDelta can apply collection-specific merge logic
            onSharedId: ({ id, decision }) => {
              const { localTs, cloudTs, localHasTs, cloudHasTs, localWins } = decision;
              const localNewerLost = localHasTs && localTs > cloudTs && !localWins;
              track1.logAnomaly('[PULL-CLOBBER]', {
                collection,
                orgId: getOrgId(),
                vesselId,
                id,
                winner: localWins ? 'local' : 'cloud',
                local_ts_norm: localTs,
                cloud_ts_norm: cloudTs,
                local_has_ts: localHasTs,
                cloud_has_ts: cloudHasTs,
                localNewerLost,
              });
            },
          }
        );

        // 3. RECONSTRUCT: Build the final merged array (only items with IDs)
        const mergedList = mergedFromDelta;

        if (skippedCount > 0) {
          devLog(`[SYNC] Delta merge for ${baseKey}: ${existingItems.length} existing + ${valueToHydrate.length} cloud → ${mergedList.length} total (${skippedCount} items skipped - no IDs)`);
        } else {
          devLog(`[SYNC] Delta merge for ${baseKey}: ${existingItems.length} existing + ${valueToHydrate.length} cloud → ${mergedList.length} total`);
        }

        // 4. FT-86: custom_maintenance_jobs filtering removed - fully migrated to maintenance_jobs (2026-03-28)

        // 5. UPDATE: Use the merged list as the new value
        valueToHydrate = mergedList;
      }

      // 🔧 BUG 2 FIX (2026-02-19): Write directly to localStorage for ARRAY_COLLECTIONS
      // Events may be dispatched before component hooks mount, losing the data
      // Solution: Write to localStorage FIRST, then dispatch event for React state updates
      // SCOPE: Only for ARRAY_COLLECTIONS (non-array modules would fail on .map())

      // ── FT-344 BLOCKER 2 (2026-07-08): PHOTOS BYPASS ─────────────────────
      // `sr.photos.v1` records carry base64 blobs (~MB each). The generic
      // ARRAY_COLLECTIONS branch below would call
      //   storageRouter.writeJSON('photos_' + vesselId, merged)
      // which persists the entire base64 array to `db.storage` alongside the
      // correct `db.photos` / `db.photoThumbs` tables — massive IDB bloat AND
      // fires a spurious `sr:storage:set` event (only the FT-68 cooldown
      // prevents an immediate re-push loop; that cooldown is a race guard,
      // not architectural protection).
      //
      // Photos flow through the `photoPullMerge` listener path EXCLUSIVELY:
      // dispatch the `sr:sync:hydrate` event and RETURN before touching
      // `storageRouter`. `photoPullMerge.installPhotoPullListener` intercepts
      // the event, decodes base64 → Blob, and writes to the correct IDB
      // tables. See src/sync/photoPullMerge.js and DESIGN-BRIEF §3.6.
      if (collection === 'sr.photos.v1') {
        window.dispatchEvent(new CustomEvent('sr:sync:hydrate', {
          detail: {
            module: baseKey, // 'photos'
            vesselId,
            value: Array.isArray(valueToHydrate) ? valueToHydrate : [],
            source: 'cloud',
            timestamp: Date.now(),
          },
        }));
        return; // MUST NOT call storageRouter.writeJSON for photos.
      }

      if (ARRAY_COLLECTIONS.has(collection)) {
        // FT-116 FIX: Org-scoped modules (like fleet_risk_assessments) don't have vesselId suffix
        // FT-182 FIX: Use toOrgScopedKey to write to correct org-prefixed key (e.g., maintenance_jobs_orgId_vesselId)
        // Without this, data is written to wrong key and archived items get lost
        const isOrgScopedModule = ORG_SCOPED_MODULES.has(baseKey);
        const fullStorageKey = toOrgScopedKey(isOrgScopedModule ? baseKey : `${baseKey}_${vesselId}`);

        // Read existing data from localStorage
        let existingData = [];
        try {
          const raw = localStorage.getItem(fullStorageKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            existingData = Array.isArray(parsed) ? parsed : [];
          }
        } catch (e) {
          devLog(`[SYNC] Error reading existing data for ${fullStorageKey}:`, e);
        }

        // Merge: Keep local-only items, update with cloud data.
        // FT-296: shared-id winners are ALREADY resolved by the delta-merge
        // above (the single authoritative timestamp gate) — `valueToHydrate`
        // holds the per-id winner (local kept when strictly newer). `localOnly`
        // is DISJOINT from valueToHydrate ids, so this only appends items that
        // exist locally but weren't in the pull; it never re-decides a shared id.
        const cloudIds = new Set(valueToHydrate.map(item => item?.id).filter(Boolean));
        const localOnly = existingData.filter(item => item?.id && !cloudIds.has(item.id));
        const merged = [...valueToHydrate, ...localOnly];

        // TASK 2 FIX: Write through storageRouter to BOTH localStorage + IndexedDB
        // Previously this only wrote to localStorage, causing Phase 2 re-hydration
        // to read from IndexedDB (empty) and overwrite React state with empty array
        // FT-457 (echo source A — ARRAY branch): tag as 'cloud-hydrate' so the
        // sr:storage:set echo this write fires is recognised + DROPPED by
        // handleStorageSet inside the justPulled cooldown, while a GENUINE
        // untagged cold-start user write in the same window is now KEPT.
        storageRouter.writeJSON(fullStorageKey, merged, { source: 'cloud-hydrate' }).catch(err => {
          devError(`[SYNC] storageRouter.writeJSON failed for ${fullStorageKey}:`, err);
        });
        devLog(`[SYNC] BUG 2 FIX: Wrote ${merged.length} items to ${fullStorageKey} (via storageRouter)`);

        // FT-68 FIX: Mark this collection as just-pulled to prevent immediate re-push
        justPulledCollections.current.set(collection, Date.now());
        devLog(`[SYNC] FT-68: Marked ${collection} as just-pulled (cooldown ${JUST_PULLED_COOLDOWN}ms)`);

        // Update valueToHydrate for the event
        valueToHydrate = merged;
      } else if (valueToHydrate && typeof valueToHydrate === 'object' && !Array.isArray(valueToHydrate)) {
        // FT-04c FIX (2026-03-16): Handle object-type modules (like maintenance_config)
        // These are NOT in ARRAY_COLLECTIONS, so the array branch above is skipped.
        // Without this, object data only dispatches a hydrate event, which is lost if
        // the consuming hook (useMaintenanceConfig) hasn't mounted yet.
        // Solution: Write directly to localStorage, same pattern as array collections.
        // FT-144 FIX (2026-04-12): Use org-scoped key so useStorageState can find it
        // Without this, object modules (maintenance_config, job_descriptions, drills_config, etc.)
        // were written to wrong key and never displayed on synced devices.
        const fullStorageKey = toOrgScopedKey(`${baseKey}_${vesselId}`);

        // FT-457 (echo source A — object-module branch): same cloud-hydrate tag
        // as the ARRAY branch above. Object modules (maintenance_config,
        // job_descriptions, drills_config, ...) fire the same echo through the
        // same storageIDB.writeJSON path, so their echo must be tagged too.
        storageRouter.writeJSON(fullStorageKey, valueToHydrate, { source: 'cloud-hydrate' }).catch(err => {
          devError(`[SYNC] storageRouter.writeJSON failed for ${fullStorageKey}:`, err);
        });
        devLog(`[SYNC] FT-144 FIX: Wrote object data to ${fullStorageKey} (org-scoped via storageRouter)`);
      }

      // THEN dispatch event for any mounted components to update React state
      // (runs for ALL collections, array and non-array)
      window.dispatchEvent(new CustomEvent('sr:sync:hydrate', {
        detail: {
          module: baseKey,  // Always base key, not date-scoped
          vesselId,
          value: valueToHydrate,
          source: 'cloud',
          timestamp: Date.now(),
          // Include date-scoped key separately if needed
          storageKey: storageKey !== baseKey ? storageKey : undefined
        }
      }));
    });
  }, [vesselId]);

  // Subscribe to cloud collections for this vessel
  const subscribeToCollections = useCallback(() => {
    if (!enabled || !vesselId) return;

    // Subscribe to each collection
    Object.entries(MODULE_MAP).forEach(([module, collection]) => {
      // FT-05 Commit 12: Org-scoped modules use BARE org ID (no prefix)
      // Same fix as Commit 11 lines 291 and 860 - subscribe must match server
      const isOrgScoped = ORG_SCOPED_MODULES.has(module);
      const subscribeId = isOrgScoped
        ? (getOrgId() || 'default')
        : vesselId;

      if (isOrgScoped) {
        devLog(`[SYNC] Subscribing to org-scoped module: ${module} with ID: ${subscribeId}`);
      }

      const unsubscribe = getCloudClient().subscribe(collection, subscribeId, (data) => {
        handleCloudUpdate(collection, data);
      });

      subscriptions.current[collection] = unsubscribe;
    });
  }, [vesselId, enabled, handleCloudUpdate]);

  // Initial sync on mount
  const performInitialSync = useCallback(async () => {
    if (!enabled || !vesselId) return;

    // FT-245: snapshot vesselId at invocation. The useCallback closure captures
    // vesselId by VALUE, so reading `vesselId` inside this loop always returns
    // the value at the moment React rebuilt this closure. If the user switches
    // vessels mid-loop, this snapshot stays at A while liveVesselIdRef.current
    // (updated synchronously by the FT-245 reset effect) tracks the new vessel.
    // We use the mismatch between the two to discard A's late writes that would
    // otherwise corrupt B's freshly reset lastSyncTime.
    const invocationVesselId = vesselId;

    devLog(`[SYNC] Performing initial sync for vessel ${vesselId}`);

    // Emit sync start event
    if (!initialSyncComplete.current) {
      window.dispatchEvent(new CustomEvent('sr:sync:status', {
        detail: { status: 'syncing', vesselId }
      }));
    }

    // RATE LIMIT FIX: Add delay between requests to stay under Azure limits
    const DELAY_BETWEEN_REQUESTS = 200; // 200ms = max 5 requests/second = 300/minute (safe)

    // Pull all collections WITH DELAYS
    const moduleEntries = Object.entries(MODULE_MAP);

    // FT-15 Part 2: Track modules where cloud returned 0 docs (for startup reconciliation)
    const emptyCloudModules = [];

    for (let i = 0; i < moduleEntries.length; i++) {
      const [module, mapping] = moduleEntries[i];

      // FIX [object Object] BUG: Extract collection string from adapter objects
      let collection;
      if (typeof mapping === 'string') {
        collection = mapping;
      } else if (typeof mapping === 'object' && mapping.collection) {
        collection = mapping.collection;
      } else {
        devLog(`[SYNC] Skipping invalid mapping for ${module}:`, mapping);
        continue;
      }

      try {
        const since = lastSyncTime.current[collection] || 0;
        // FT-05 Commit 11: Org-scoped modules use BARE org ID (no prefix)
        // Server expects raw orgId from JWT for vesselId comparison
        const isOrgScoped = ORG_SCOPED_MODULES.has(module);
        const pullId = isOrgScoped
          ? (getOrgId() || 'default')
          : vesselId;

        if (isOrgScoped) {
          devLog(`[SYNC] Pulling org-scoped module: ${module} with ID: ${pullId}`);
        }

        // FT-365 v3 Layer B#4: register AbortController so a user-boundary
        // wipe can invalidate the pull result before it reaches the hydrate
        // event. On abort, treat as skip — no local state change.
        const ft365PullController = new AbortController();
        const pullResult = await wrapWithAbortSignal(
          ft365PullController,
          () => getCloudClient().pull(collection, pullId, since)
        );
        if (pullResult && pullResult.aborted) {
          devLog(`[SYNC] FT-365 v3: pull aborted mid-flight for ${collection}`);
          continue;
        }

        // 🔴 FT-49: Check for error signal BEFORE processing (2026-03-23)
        // Failed pulls (403, network errors) now return { error: true, status, data: [] }
        // instead of silently returning [] which would destroy localStorage
        if (pullResult?.error) {
          devLog(`[SYNC] FT-49 Pull FAILED for ${collection} (status: ${pullResult.status}) - keeping local data`);
          continue;  // Skip this collection, don't hydrate, don't update lastSyncTime
        }

        // Extract array from result (could be direct array or wrapped in { data: [] })
        const updates = Array.isArray(pullResult) ? pullResult : (pullResult?.data || []);

        // Guard: Only process if we got a valid array with data
        if (!Array.isArray(updates)) {
          devLog(`[SYNC] Invalid response for ${collection}, expected array but got ${typeof updates}`);
          continue;  // Skip this collection, don't update lastSyncTime
        }

        if (updates.length === 0) {
          devLog(`[SYNC] No updates for ${collection}, skipping hydration`);
          // FT-15 Part 2: Track empty cloud modules for reconciliation
          emptyCloudModules.push({ module, collection });
          continue;  // Don't update lastSyncTime if no data received
        }

        // CRITICAL FIX: Handle array vs single-document collections differently
        if (ARRAY_COLLECTIONS.has(collection)) {
          // Array-based collections: Aggregate all documents into a single array
          devLog(`[SYNC] Aggregating ${updates.length} documents for array collection ${collection}`);

          // Backend returns array of: { id, collection, data: { items: [...], _isArray: true }, ... }
          // We need to extract items from each wrapper and flatten into a single array
          const aggregatedArray = updates.flatMap(update => {
            const wrapper = update.data || update;

            // If wrapper has items array, extract it
            if (wrapper && wrapper._isArray && Array.isArray(wrapper.items)) {
              return wrapper.items;
            }

            // If wrapper is already an array, use it directly
            if (Array.isArray(wrapper)) {
              return wrapper;
            }

            // Single item - wrap in array for flatMap
            return [wrapper];
          });

          devLog(`[SYNC] Extracted ${aggregatedArray.length} items from ${updates.length} wrappers for ${collection}`);

          // Call handleCloudUpdate ONCE with the complete array
          handleCloudUpdate(collection, aggregatedArray);
        } else {
          // Single-document collections: Process individually
          devLog(`[SYNC] Processing ${updates.length} documents individually for ${collection}`);

          updates.forEach(update => {
            const payload = update.data || update;
            handleCloudUpdate(collection, payload);
          });
        }

        // FT-245: race guard — if the user switched vessels while this pull was
        // in flight, liveVesselIdRef.current now holds the NEW vesselId while
        // invocationVesselId still holds OURS (A). Writing Date.now() to
        // lastSyncTime here would re-poison B's freshly reset state and
        // reproduce the exact bug FT-245 fixes. Skip the write; the next pull
        // for the current vessel will set its own timestamp correctly.
        if (liveVesselIdRef.current !== invocationVesselId) {
          devLog(`[SYNC] FT-245: vessel switched during pull (${invocationVesselId} → ${liveVesselIdRef.current}) — discarding late lastSyncTime write for ${collection}`);
          continue;
        }
        lastSyncTime.current[collection] = Date.now();
      } catch (error) {
        devLog(`[SYNC] Error syncing ${collection}:`, error);
        // Don't update lastSyncTime on error - will retry with same timestamp
      }

      // RATE LIMIT FIX: Delay before next request (except last iteration)
      if (i < moduleEntries.length - 1) {
        await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_REQUESTS));
      }
    }

    devLog(`[SYNC] Initial sync complete for ${moduleEntries.length} collections`);

    // FT-15 Part 2: STARTUP RECONCILIATION
    // Check if local has data for modules where cloud returned 0 docs → force push
    if (!reconciliationComplete.current && emptyCloudModules.length > 0) {
      devLog(`[SYNC] Starting startup reconciliation for ${emptyCloudModules.length} empty cloud modules`);

      // FT-60 FIX: Get orgId for exact key matching
      const orgId = getOrgId();

      for (const { module, collection } of emptyCloudModules) {
        try {
          // FT-60 FIX: Construct the EXACT expected key for this module + org + vessel
          // Key format: module_orgId_vesselId (three segments)
          // This prevents reading data from OTHER vessels' localStorage keys
          const expectedKey = `${module}_${orgId}_${vesselId}`;

          devLog(`[SYNC] FT-60: Looking for exact key: ${expectedKey}`);

          let localRaw = localStorage.getItem(expectedKey);
          let matchedKey = localRaw ? expectedKey : null;

          // FT-60: If exact key not found, DON'T fallback to prefix matching
          // The prefix matching was the bug - it grabbed other vessels' data

          if (!localRaw || localRaw === '[]' || localRaw === '{}') {
            devLog(`[SYNC] FT-60: No local data found for exact key ${expectedKey} (this is correct - prevents cross-vessel contamination)`);
            continue;
          }

          devLog(`[SYNC] FT-60 Reconciliation: found local data in exact key ${matchedKey}`);

          const localData = JSON.parse(localRaw);

          // Check if local data is genuinely non-empty
          const hasData = Array.isArray(localData)
            ? localData.length > 0
            : (localData && typeof localData === 'object' && Object.keys(localData).length > 0);

          if (!hasData) {
            devLog(`[SYNC] Reconciliation: Local data empty for ${module}`);
            continue;
          }

          const itemCount = Array.isArray(localData) ? localData.length : Object.keys(localData).length;
          devLog(`[SYNC] Startup reconciliation: pushing ${module} (local has ${itemCount} items, cloud is empty)`);

          // Build the sync data and push
          const cloudDocId = `${vesselId}_${module}`;
          const syncData = Array.isArray(localData)
            ? {
                items: localData,
                _isArray: true,
                vesselId,
                updatedAt: Date.now(),
                syncedAt: Date.now()
              }
            : {
                ...localData,
                vesselId,
                updatedAt: Date.now(),
                syncedAt: Date.now()
              };

          // Push directly (don't queue - this is a one-time reconciliation)
          // FT-365 v3 R2 blocker 7: wrap in AbortController so a user-boundary
          // wipe mid-reconciliation invalidates the response.
          const ft365ReconController = new AbortController();
          const reconResult = await wrapWithAbortSignal(
            ft365ReconController,
            () => getCloudClient().push(collection, cloudDocId, syncData)
          );
          if (reconResult && reconResult.aborted) {
            devLog(`[SYNC] FT-365 v3: reconciliation push aborted mid-flight for ${module}`);
            continue;
          }
          devLog(`[SYNC] Reconciliation push complete for ${module}`);

          // Rate limit
          await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_REQUESTS));
        } catch (error) {
          devError(`[SYNC] Reconciliation error for ${module}:`, error);
        }
      }

      reconciliationComplete.current = true;
      devLog(`[SYNC] Startup reconciliation complete`);
    }

    // Mark sync complete and emit event
    initialSyncComplete.current = true;
    window.dispatchEvent(new CustomEvent('sr:sync:status', {
      detail: { status: 'complete', vesselId }
    }));
  }, [vesselId, enabled, handleCloudUpdate]);

  // Setup event listeners and subscriptions
  useEffect(() => {
    if (!enabled) return;

    // Listen for local storage events
    window.addEventListener('sr:storage:set', handleStorageSet);

    // Subscribe to cloud updates
    subscribeToCollections();

    // 🛡️ FT-234: Flush persistent outbox BEFORE pulling from cloud
    // This ensures offline changes are pushed to server before we pull,
    // preventing cloud data from overwriting unsync'd local changes
    // performInitialSync() is inside the IIFE to avoid race condition
    (async () => {
      try {
        // 🛡️ FT-405 (2026-07-26): revive any items stranded at status 'failed'
        // (retryCount < 3) BEFORE the drain. processOutbox only pushes 'pending'
        // items, and retryFailedItems() was defined but never called — so a
        // genuinely-failed item was stuck forever. Flipping 'failed'→'pending'
        // here gives it another chance at startup. No-op if nothing is failed.
        await retryFailedItems();
        const result = await processOutbox(getCloudClient());
        if (result.success > 0 || result.failed > 0) {
          devLog(`[SYNC] FT-234: Startup outbox flush: ${result.success} succeeded, ${result.failed} failed`);
        }
      } catch (err) {
        devError('[SYNC] FT-234: Startup outbox flush failed:', err);
      }
      performInitialSync();
    })();

    // Auto-Heal Corruption on Mount
    cleanupStorageCorruption(vesselId);

    // Periodic sync (5 minute full pull)
    const syncInterval = setInterval(() => {
      processSyncQueue();
      performInitialSync();
    }, SYNC_POLICY.syncInterval);

    // 🛡️ FT-05 Commit 9 (2026-03-22): Safety Net Queue Drain Interval
    // Checks every 5 seconds for orphaned queue items that weren't processed
    // due to edge cases (e.g., app was backgrounded, rapid writes during processing)
    // This is a BACKUP to the continuous drain in finally{} — most items won't need this
    const queueDrainInterval = setInterval(() => {
      if (syncQueue.current.length > 0 && !isProcessing.current) {
        devLog(`[SYNC] FT-05: Safety net triggered — draining ${syncQueue.current.length} orphaned items`);
        processSyncQueue();
      }
    }, 5000);

    // FT-20: Online event listener - sync when network restored
    const handleOnline = async () => {
      devLog('[SYNC] Network restored — triggering full sync');

      // 🛡️ FT-234: Flush persistent outbox FIRST (before pull)
      // This pushes any items that were queued while offline and survived page refresh
      try {
        // 🛡️ FT-405 (2026-07-26): revive sticky-'failed' items (retryCount < 3)
        // BEFORE the reconnect drain, so an item whose FIRST reconnect push
        // failed gets retried on a LATER reconnect — without needing a second
        // unrelated save to overwrite the whole collection. processOutbox only
        // pushes 'pending'; retryFailedItems flips eligible 'failed'→'pending'.
        await retryFailedItems();
        const result = await processOutbox(getCloudClient());
        if (result.success > 0 || result.failed > 0) {
          devLog(`[SYNC] FT-234: Reconnect outbox flush: ${result.success} succeeded, ${result.failed} failed`);
        }
      } catch (err) {
        devError('[SYNC] FT-234: Reconnect outbox flush failed:', err);
      }

      // Pull latest from cloud
      performInitialSync();
      // Push any pending local changes (in-memory queue)
      processSyncQueue();

      // FT-379: opportunistically drain the anomaly queue to the sink on
      // reconnect. Fire-and-forget — NOT awaited, all errors swallowed inside
      // flush(); must never influence the sync critical path above.
      try { track1.flush(); } catch (_) { /* telemetry never breaks reconnect */ }
    };

    // FT-20: Force sync event listener - manual sync trigger from UI
    const handleForceSync = () => {
      devLog('[SYNC] Force sync triggered — performing full sync');
      // Pull latest from cloud
      performInitialSync();
      // Push any pending local changes
      processSyncQueue();
      // Emit completion event for UI feedback
      window.dispatchEvent(new CustomEvent('sr:sync:force-sync-complete', {
        detail: { vesselId, timestamp: Date.now() }
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('sr:sync:force-sync', handleForceSync);

    // Cleanup
    return () => {
      window.removeEventListener('sr:storage:set', handleStorageSet);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('sr:sync:force-sync', handleForceSync);

      // Unsubscribe from all collections
      Object.values(subscriptions.current).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });

      clearInterval(syncInterval);
      clearInterval(queueDrainInterval);  // FT-05 Commit 9
    };
  }, [enabled, handleStorageSet, subscribeToCollections, performInitialSync, processSyncQueue, vesselId]);

  // Listen for hydrate events and update local storage
  useEffect(() => {
    const handleHydrate = (event) => {
      const { module, vesselId: hydrateVesselId, value } = event.detail;

      // Only process hydrate events for current vessel
      if (hydrateVesselId !== vesselId) return;

      devLog(`[SYNC] Hydrating ${module} for vessel ${vesselId}`);

      // Legacy hooks will handle actual storage write
      // This is just for logging/monitoring
    };

    window.addEventListener('sr:sync:hydrate', handleHydrate);

    return () => {
      window.removeEventListener('sr:sync:hydrate', handleHydrate);
    };
  }, [vesselId]);

  // Provide sync status via data attribute for debugging
  useEffect(() => {
    if (enabled) {
      document.body.setAttribute('data-sync-bridge', 'active');
      document.body.setAttribute('data-sync-vessel', vesselId || 'none');
    } else {
      document.body.removeAttribute('data-sync-bridge');
      document.body.removeAttribute('data-sync-vessel');
    }

    return () => {
      document.body.removeAttribute('data-sync-bridge');
      document.body.removeAttribute('data-sync-vessel');
    };
  }, [enabled, vesselId]);

  // 🛡️ FORCE-PULL EVENT HANDLER: Recovery mechanism when empty push is blocked
  // When the cloud-aware guard blocks an empty push, it triggers this to restore local data
  useEffect(() => {
    const handleForcePull = async (event) => {
      const { collection, vesselId: eventVesselId, reason } = event.detail;

      if (eventVesselId !== vesselId) return;

      devLog(`[SYNC] 🔄 Force pull triggered for ${collection} (reason: ${reason})`);

      try {
        // FT-365 v3 R2 blocker 7: wrap in AbortController so a user-boundary
        // wipe mid-force-pull discards the response before it hydrates state.
        const ft365ForcePullController = new AbortController();
        const pullResult = await wrapWithAbortSignal(
          ft365ForcePullController,
          () => getCloudClient().pull(collection, eventVesselId, 0)
        );
        if (pullResult && pullResult.aborted) {
          devLog(`[SYNC] FT-365 v3: force-pull aborted mid-flight for ${collection}`);
          return;
        }

        // 🔴 FT-49: Check for error signal (2026-03-23)
        if (pullResult?.error) {
          devLog(`[SYNC] FT-49 Force pull failed for ${collection} (status: ${pullResult.status}) - keeping local data`);
          return;
        }
        const updates = Array.isArray(pullResult) ? pullResult : (pullResult?.data || []);

        // LOOP PROTECTION: If cloud is also empty, do NOT re-trigger any push
        if (!Array.isArray(updates) || updates.length === 0) {
          devLog(`[SYNC] Force pull returned empty for ${collection} — no action taken (loop protection)`);
          return;
        }

        // Aggregate and hydrate (same logic as performInitialSync for array collections)
        if (ARRAY_COLLECTIONS.has(collection)) {
          const aggregatedArray = updates.flatMap(update => {
            const wrapper = update.data || update;
            if (wrapper && wrapper._isArray && Array.isArray(wrapper.items)) {
              return wrapper.items;
            }
            if (Array.isArray(wrapper)) {
              return wrapper;
            }
            return [wrapper];
          });

          if (aggregatedArray.length > 0) {
            handleCloudUpdate(collection, aggregatedArray);
            devLog(`[SYNC] ✅ Force pull restored ${aggregatedArray.length} records for ${collection}`);
          }
        } else {
          // Single-document collection
          updates.forEach(update => {
            const payload = update.data || update;
            handleCloudUpdate(collection, payload);
          });
          devLog(`[SYNC] ✅ Force pull processed ${updates.length} documents for ${collection}`);
        }
      } catch (error) {
        devError(`[SYNC] ❌ Force pull failed for ${collection}:`, error);
      }
    };

    window.addEventListener('sr:sync:force-pull', handleForcePull);
    return () => window.removeEventListener('sr:sync:force-pull', handleForcePull);
  }, [vesselId, handleCloudUpdate]);

  // =========================================================================
  // FT-436 (VESSELS-ONLY) — fleet-card cross-device PROFILE-photo pull.
  //
  // The fleet landing renders one photo card per assigned vessel, each reading
  // its image from IDB at `profile:<vesselId>`. But the normal vessel pull only
  // hydrates the CURRENT vessel's photos — so on a FRESH device every OTHER
  // vessel card had no photo in IDB and showed "No photo yet", even though the
  // cloud held it. This pulls `sr.photos.v1` for the OTHER assigned vessels and
  // hydrates ONLY the `entity_profile` records via the EXISTING photoPullMerge
  // listener (the same `sr:sync:hydrate` {module:'photos'} event vessel photos
  // already use).
  //
  // STRICTLY ADDITIVE + READ-ONLY: the client's pull never mutates the cloud, so
  // the crew-view-only gate is preserved (an admin-set photo simply DISPLAYS).
  // Only vessels in the server-scoped list are iterated (FT-435 assignment
  // scoping). It does NOT touch the LWW merge, the FT-453 justPulled cooldown, or
  // the FT-234 outbox — it rides the separate record-photo pipeline only. The
  // CURRENT vessel is skipped here (its own pull covers it — a redundant round
  // trip, harmless if not skipped).
  // =========================================================================
  const performFleetProfilePhotoPull = useCallback(async () => {
    if (!enabled) return;
    if (!isAzureApiConfigured()) return;

    await pullFleetProfilePhotos({
      pull: (collection, entityId, since) => getCloudClient().pull(collection, entityId, since),
      vessels: liveVesselsRef.current || [],
      skipVesselId: liveVesselIdRef.current,  // covered by the current vessel's own pull
      onError: (msg, err) => (err ? devError(`[SYNC] FT-436: ${msg}`, err) : devLog(`[SYNC] FT-436: ${msg}`)),
    });
  }, [enabled]);

  // Fire the fleet-wide profile-photo pull once the vessel list is available (and
  // whenever it changes — e.g. a newly-assigned vessel). Reads the list via a ref,
  // so re-running on identity changes is harmless/idempotent (photoPullMerge's
  // staleness guard skips already-current rows).
  useEffect(() => {
    if (!enabled) return;
    if (!Array.isArray(vessels) || vessels.length === 0) return;
    performFleetProfilePhotoPull();
  }, [enabled, vessels, performFleetProfilePhotoPull]);

  // Also re-pull fleet profile photos on an explicit force-sync from the UI.
  useEffect(() => {
    const onForceSync = () => { performFleetProfilePhotoPull(); };
    window.addEventListener('sr:sync:force-sync', onForceSync);
    return () => window.removeEventListener('sr:sync:force-sync', onForceSync);
  }, [performFleetProfilePhotoPull]);

  return <>{children}</>;
};

export default UniversalSyncBridge;